import { useI18n } from "react-simple-i18n";
import { styled } from "@mui/material/styles";
import { Box, Stack, Typography } from "@mui/material";
import { useGlobalContext } from "../provider";
import { Layout } from "../components/layout/layout";
import { restApi } from "../provider/restApi";
import { GlobalFirstSection, GlobalSpacing } from "../components/layout/styles";
import { TextField } from "../components/inputs/textfield";
import { FirstButton } from "../components/buttons/buttons";
import { dashboardImg } from "../assets/image";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
	const { t } = useI18n();
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		name: localStorage.getItem("name") || '',
		email: localStorage.getItem("email") || '',
		subject: '',
		message: ''
	});
	const [globalState, { dispatch }]: GlobalContextType = useGlobalContext();
	const [errors, setErrors] = useState({});

	const validate = () => {
		const newErrors = {};
		if (!formData.name) (newErrors as { name?: string }).name = t("validation.nameRequired");
		if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) (newErrors as { email?: string }).email = t("validation.emailInvalid");
		if (!formData.subject) (newErrors as { subject?: string }).subject = t("validation.subjectRequired");
		if (!formData.message) (newErrors as { message?: string }).message = t("validation.messageRequired");
		return newErrors;
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value
		});
	};

	const handleSubmit = async () => {
		const validationErrors = validate();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}

		dispatch({ loading: true });
		try {
			const res = await restApi.sendEmail(formData);
			if (res) {
				console.log("Successfully submitted");
				setFormData({
					name: localStorage.getItem("name") || '',
					email: localStorage.getItem("email") || '',
					subject: '',
					message: ''
				});
			}
		} catch (error) {
			console.error("Error sending email:", error);
		} finally {
			dispatch({ loading: false });
		}
	};

	useEffect(() => {
		const isAuthorized = localStorage.getItem("isAuthorized");
		if (isAuthorized !== 'true') {
			navigate('/login');
		}
	}, [navigate]);

	return (
		<Layout>
			<ContactBackground>
				<ContactWrapper>
					<Stack textAlign="center" maxWidth={500} gap={2}>
						<Typography variant="h2">
							{t("contact.title")}
						</Typography>
						<Typography variant="body1">
							{t("contact.description")}
						</Typography>
					</Stack>

					<ContactUsForm>
						<TextField
							label={t("inputLabels.name")}
							name="name"
							value={formData.name}
							onChange={handleChange}
							error={!!(errors as { name?: string }).name}
							helperText={(errors as { name?: string }).name}
						/>
						<TextField
							label={t("inputLabels.email")}
							name="email"
							value={formData.email}
							onChange={handleChange}
							error={!!(errors as { email?: string }).email}
							helperText={(errors as { email?: string }).email}
						/>
						<TextField
							label={t("inputLabels.subject")}
							name="subject"
							value={formData.subject}
							onChange={handleChange}
							error={!!(errors as { subject?: string }).subject}
							helperText={(errors as { subject?: string }).subject}
						/>
						<TextField
							label={t("inputLabels.message")}
							name="message"
							value={formData.message}
							onChange={handleChange}
							multiline
							rows={4}
							error={!!(errors as { message?: string }).message}
							helperText={(errors as { message?: string }).message}
						/>
					</ContactUsForm>

					<FirstButton onClick={handleSubmit} disabled={globalState.loading}>
						<Typography variant="body1">
							{globalState.loading ? t("buttons.sending") : t("buttons.sendMessage")}
						</Typography>
					</FirstButton>
				</ContactWrapper>
			</ContactBackground>
		</Layout>
	)
}

const ContactBackground = styled(GlobalSpacing)({
	backgroundImage: `url(${dashboardImg})`,
	backgroundRepeat: "no-repeat",
	backgroundSize: 'cover',
})

const ContactWrapper = styled(GlobalFirstSection)(({ theme }) => ({
	display: 'flex',
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "space-evenly",
	gap: theme.spacing(3),
	padding: `${theme.spacing(15)} 0px`,

	"h2, p, label, input": {
		color: theme.palette.common.white,
	},

	button: {
		width: "100%",
		maxWidth: 550,
		marginTop: theme.spacing(2),
	},

	[theme.breakpoints.down("md")]: {
		padding: `${theme.spacing(8)} 0px`,
		gap: theme.spacing(2),

		button: {
			marginTop: theme.spacing(1),
		}
	}
}))

const ContactUsForm = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),
	paddingTop: theme.spacing(3),

	width: "100%",
	maxWidth: 550,

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
		paddingTop: theme.spacing(2),
	}
}))

export { Contact }